import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { FaTrash } from "react-icons/fa";
import {
  generateShortItemCode,
  intt,
  threeDecimal,
} from "../../config/helperFunctions";
import { useDebounce } from "../../hooks/useDebounced";
import { fetchAllLabelledStocks } from "../../services/labelled-stock-service";
import { useQuery } from "@tanstack/react-query";

const InvoiceItemForm = ({
  control,
  labelledStockFields,
  rowindex,
  register,
  errors,
  watch,
  firstFieldRef,
  onDelete,
  setValue,
}) => {
  const categoriesFromState = useSelector(
    (state) => state.categories?.categories
  );
  const productsFromState = useSelector((state) => state.products?.products);
  const vendorsFromState = useSelector((state) => state.vendors?.vendors);
  const designFromState = useSelector((state) => state.designs?.designs);
  const purityFromState = useSelector((state) => state.purities?.purities);
  const branchesFromState = useSelector((state) => state.branches?.branches);

  const [searchQuery, setSearchQuery] = useState("null");
  const debouncedSearch = useDebounce(searchQuery, 2000);
  const { data: stocks } = useQuery({
    queryKey: ["/sales/labelled-stocks", debouncedSearch ?? ""],
    queryFn: ({ queryKey, signal }) =>
      fetchAllLabelledStocks({ queryKey, signal }),
    refetchOnWindowFocus: false,
  });
  const itemCodeDetail = watch(`invoiceEntries.${rowindex}.itemCodeDetail`);
  const category = watch(`invoiceEntries.${rowindex}.category`);
  const product = watch(`invoiceEntries.${rowindex}.product`);
  const totalWt = watch(`invoiceEntries.${rowindex}.totalWt`);
  const packingWt = watch(`invoiceEntries.${rowindex}.packingWt`);
  const stoneWt = watch(`invoiceEntries.${rowindex}.stoneWt`);
  const diamondWt = watch(`invoiceEntries.${rowindex}.diamondWt`);

  const formRef = useRef(null);

  // 👇 Add keyboard shortcut for Ctrl + Delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete]);

  useEffect(() => {
  if (rowindex !== 0) return; // set global listener only once

  const pressed = new Set();
  let comboTimer = null;

  const resetCombo = () => {
    pressed.clear();
    if (comboTimer) {
      clearTimeout(comboTimer);
      comboTimer = null;
    }
  };

  const tryApplyRateToAll = () => {
    // Check combo within a short window
    if (pressed.has("Control") && pressed.has("r") && pressed.has("a")) {
      const entries = watch("invoiceEntries") || [];
      if (!entries.length) return;
      const firstVal = watch(`invoiceEntries.0.${"ratePerGram"}`);
      for (let i = 0; i < entries.length; i++) {
        setValue(`invoiceEntries.${i}.${"ratePerGram"}`, firstVal, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      resetCombo();
    }
  };

  const onKeyDown = (e) => {
    // Normalize keys
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (key === "r" || key === "a" || key === "Control") {
      pressed.add(key);
      // stop default reload on Ctrl+R
      if (e.ctrlKey && key === "r") e.preventDefault();
      // start/refresh a short window for the combo
      if (comboTimer) clearTimeout(comboTimer);
      comboTimer = setTimeout(resetCombo, 600); // 600ms window
      tryApplyRateToAll();
    }
  };

  const onKeyUp = (e) => {
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    pressed.delete(key);
  };

  window.addEventListener("keydown", onKeyDown, { capture: true });
  window.addEventListener("keyup", onKeyUp, { capture: true });
  return () => {
    window.removeEventListener("keydown", onKeyDown, { capture: true });
    window.removeEventListener("keyup", onKeyUp, { capture: true });
    resetCombo();
  };
}, [rowindex, watch, setValue]);


  // ====================== business logic goes here ==================

  useEffect(() => {
    setValue(`invoiceEntries.${rowindex}.category`, intt(itemCodeDetail?.extra?.categoryId) ? {
      value: intt(itemCodeDetail?.extra?.categoryId),
      label: itemCodeDetail?.extra?.categoryName,
    }:"");
    setValue(`invoiceEntries.${rowindex}.product`,intt(itemCodeDetail?.extra?.productId) ? {
      value: intt(itemCodeDetail?.extra?.productId),
      label: itemCodeDetail?.extra?.productName,
    }:"");
    setValue(`invoiceEntries.${rowindex}.design`, intt(itemCodeDetail?.extra?.designId) ? {
      value: intt(itemCodeDetail?.extra?.designId),
      label: itemCodeDetail?.extra?.designName,
    }:"");
    setValue(`invoiceEntries.${rowindex}.purity`,intt(itemCodeDetail?.extra?.purityId) ? {
      value: intt(itemCodeDetail?.extra?.purityId),
      label: itemCodeDetail?.extra?.purityName,
    }:"");
    setValue(
      `invoiceEntries.${rowindex}.totalWt`,
      threeDecimal(itemCodeDetail?.extra?.totalWt)
    );
    setValue(
      `invoiceEntries.${rowindex}.packingWt`,
      threeDecimal(itemCodeDetail?.extra?.packingWt)
    );
    setValue(
      `invoiceEntries.${rowindex}.grossWt`,
      threeDecimal(itemCodeDetail?.extra?.grossWt)
    );
    setValue(
      `invoiceEntries.${rowindex}.stoneWt`,
      threeDecimal(itemCodeDetail?.extra?.stoneWt)
    );
    setValue(
      `invoiceEntries.${rowindex}.diamondWt`,
      threeDecimal(itemCodeDetail?.extra?.diamondWt)
    );
    setValue(
      `invoiceEntries.${rowindex}.netWt`,
      threeDecimal(itemCodeDetail?.extra?.netWt)
    );
  }, [itemCodeDetail]);

  useEffect(() => {
    setValue(
      `invoiceEntries.${rowindex}.grossWt`,
      ((+totalWt || 0) - (+packingWt || 0))?.toFixed(3)
    );
    setValue(
      `invoiceEntries.${rowindex}.netWt`,
      (
        (+totalWt || 0) -
        (+packingWt || 0) -
        (+stoneWt || 0) -
        (+diamondWt || 0)
      )?.toFixed(3)
    );
  }, [totalWt, packingWt, stoneWt, diamondWt, setValue]);

  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: rowindex * 0.05 }}
      className="mr-2 p-4 border border-gray-200 hover:border-gray-400 shadow-lg rounded-md mb-4 bg-gray-500 relative"
    >
      {/* Header: Row Number + Trash Icon */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          #{rowindex + 1} - Stock Entry
        </h3>
        <div className="flex-1 h-px bg-gray-300 mx-2" />

        {/* <button
          type="button"
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 transition-all duration-300 hover:scale-110"
          title="Delete this item"
        > */}
        <FaTrash className="text-[14px] cursor-pointer text-white" onClick={onDelete} />
        {/* </button> */}
      </div>

      {/* Fields Grid */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-4">
        {labelledStockFields?.map((item, idx) => (
          <>
            {item?.name == "itemCodeDetail" ? (
              <div className="md:col-span-1 cursor-pointer">
                <label className="block text-xs font-medium text-white mb-1">
                  Item Code
                </label>
                <>
                 <Controller
  name={`invoiceEntries.${rowindex}.${item.name}`}
  control={control}
  render={({ field }) => (
    <Select
      {...field}
      ref={(ref) => {
        field.ref(ref);
        if (firstFieldRef) {
          firstFieldRef.current = {
            focus: () => {
              ref?.focus?.();
            },
          };
        }
      }}
      onInputChange={(e) => {
        setSearchQuery(e);
      }}
      options={
        stocks?.length
          ? stocks.map((item) => {
              return {
                value: item?.id,
                label: item?.itemCode,
                extra: item,
              };
            })
          : []
      }
      classNamePrefix="react-select"
      menuPlacement="bottom"
      isClearable
      isSearchable
      styles={{
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#9481ce" : "#ccc",
    boxShadow: state.isFocused
      ? "0 0 0 1px #9481ce"
      : "none",
    fontSize: "12px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "30px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    height: "30px",
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
    zIndex: 9999, // ensure always on top
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "120px", // 🔹 dropdown height
    overflowY: "auto",  // 🔹 scrollbar enable
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
  }),
}}

    />
  )}
/>

                </>
              </div>
            ) : (
              <div
                key={idx}
                className={
                  ["category", "product", "design"].includes(item?.name)
                    ? "md:col-span-2"
                    : "md:col-span-1"
                }
              >
                <label className="block text-xs font-medium text-white mb-1">
                  {item?.label} {item?.mandatory && <span className="text-red-400">*</span>}
                </label>

                {/* TEXT */}
                {item?.type === "text" && (
                  <>
                    <input
                      {...register(`invoiceEntries.${rowindex}.${item.name}`, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      ref={(el) => {
                        register(`invoiceEntries.${rowindex}.${item.name}`).ref(
                          el
                        );
                        if (idx === 0) firstFieldRef.current = el;
                      }}
                      disabled={
                        item.disabled ||
                        item.name == "grossWt" ||
                        item.name == "netWt"
                      }
                      //   ref={idx === 0 ? firstFieldRef : null}
                      className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    {errors?.invoiceEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.invoiceEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* NUMBER */}
                {item?.type === "number" && (
                  <>
                    <input
                      type="number"
                      {...register(`invoiceEntries.${rowindex}.${item.name}`, {
                        required: item?.mandatory 
                          ? `${item.label} is required`
                          : false,
                      })}
                      ref={(el) => {
                        register(`invoiceEntries.${rowindex}.${item.name}`).ref(
                          el
                        );
                        if (idx === 0) firstFieldRef.current = el;
                      }}
                      disabled={item.disabled}
                      //   ref={idx === 0 ? firstFieldRef : null}
                      className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    {errors?.invoiceEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.invoiceEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DATE */}
                {item?.type === "date" && (
                  <>
                    <input
                      type="date"
                      ref={(el) => {
                        register(`invoiceEntries.${rowindex}.${item.name}`).ref(
                          el
                        );
                        if (idx === 0) firstFieldRef.current = el;
                      }}
                      //   ref={idx === 0 ? firstFieldRef : null}
                      {...register(`invoiceEntries.${rowindex}.${item.name}`, {
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      })}
                      disabled={item.disabled}
                      className="w-full px-3 py-[6px] text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    {errors?.invoiceEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.invoiceEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}

                {/* DROPDOWN */}
                {item?.type === "dropdown" && (
                  <>
                    <Controller
                      name={`invoiceEntries.${rowindex}.${item.name}`}
                      control={control}
                      rules={{
                        required: item?.mandatory
                          ? `${item.label} is required`
                          : false,
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          ref={(ref) => {
                            field.ref(ref);
                            if (idx === 0 && firstFieldRef) {
                              firstFieldRef.current = {
                                focus: () => {
                                  ref?.focus?.();
                                },
                              };
                            }
                          }}
                          options={handleDropDowns({
                            key: item.name,
                            vendors: vendorsFromState,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            branches: branchesFromState,
                            category,
                            product,
                          })}
                          classNamePrefix="react-select"
                          menuPlacement="bottom"
                          isClearable
                          isDisabled={item.disabled || ((item?.name == "category" || item?.name == "product" || item?.name == "design" || item?.name == "purity" ) && itemCodeDetail?.extra )}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: "32px",
                              height: "32px",
                              borderColor: state.isFocused ? "#9481ce" : "#ccc",
                              boxShadow: state.isFocused
                                ? "0 0 0 1px #9481ce"
                                : "none",
                              fontSize: "12px",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: "30px",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "30px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                            menu: (base) => ({ ...base, fontSize: "12px" }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: "12px",
                            }),
                          }}
                        />
                      )}
                    />
                    {errors?.invoiceEntries?.[rowindex]?.[item.name] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.invoiceEntries[rowindex][item.name].message}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        ))}
      </div>
    </motion.div>
  );
};

export default InvoiceItemForm;
